import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Hash, Phone, MessageCircle, MapPin,
  AtSign, CheckCircle, XCircle, Loader, RefreshCw,
} from 'lucide-react';
import './CompleteProfile.css';

export default function CompleteProfile() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: '', rollNumber: '', callNumber: '',
    whatsappNumber: '', location: '', gender: 'prefer_not_to_say',
  });
  const [errors,         setErrors]         = useState({});
  const [loading,        setLoading]        = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [suggestions,    setSuggestions]    = useState([]);
  const [loadingSugg,    setLoadingSugg]    = useState(false);
  const checkRef = useRef(null);

  // Fetch suggestions from server (always fresh + available)
  const fetchSuggestions = async () => {
    setLoadingSugg(true);
    try {
      const { data } = await api.get('/users/username-suggestions');
      setSuggestions(data.suggestions);
      // Auto-fill first suggestion
      if (data.suggestions.length > 0 && !form.username) {
        setForm(f => ({ ...f, username: data.suggestions[0] }));
        setUsernameStatus('available');
      }
    } catch {}
    finally { setLoadingSugg(false); }
  };

  useEffect(() => { fetchSuggestions(); }, []);

  // Live check
  useEffect(() => {
    const username = form.username.trim();
    if (!username) { setUsernameStatus(null); return; }
    if (!/^[a-z0-9_]{3,20}$/.test(username)) {
      setUsernameStatus('invalid'); return;
    }
    setUsernameStatus('checking');
    clearTimeout(checkRef.current);
    checkRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/users/check-username/' + username);
        setUsernameStatus(data.available ? 'available' : 'taken');
      } catch { setUsernameStatus(null); }
    }, 500);
  }, [form.username]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const val = name === 'username'
      ? value.toLowerCase().replace(/[^a-z0-9_]/g, '')
      : value;
    setForm(f => ({ ...f, [name]: val }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    const phone = /^[6-9]\d{9}$/;
    if (!form.username.trim())            e.username       = 'Username is required.';
    if (usernameStatus === 'taken')       e.username       = 'Username is already taken.';
    if (usernameStatus === 'invalid')     e.username       = 'Invalid format.';
    if (!form.rollNumber.trim())          e.rollNumber     = 'Roll number is required.';
    if (!phone.test(form.callNumber))     e.callNumber     = 'Enter a valid 10-digit number.';
    if (!phone.test(form.whatsappNumber)) e.whatsappNumber = 'Enter a valid 10-digit number.';
    if (!form.location.trim())            e.location       = 'Location is required.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (usernameStatus !== 'available') {
      toast.error('Please choose an available username.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/users/complete-profile', form);
      updateUser(data.user);
      toast.success('Welcome to GLA Exchange, @' + form.username + ' 🎉');
      navigate('/app/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong.');
    } finally { setLoading(false); }
  };

  const StatusIcon = () => {
    if (usernameStatus === 'checking')  return <Loader size={16} className="spin" style={{ color: 'var(--text-light)' }} />;
    if (usernameStatus === 'available') return <CheckCircle size={16} style={{ color: 'var(--success)' }} />;
    if (usernameStatus === 'taken')     return <XCircle size={16} style={{ color: 'var(--danger)' }} />;
    if (usernameStatus === 'invalid')   return <XCircle size={16} style={{ color: 'var(--danger)' }} />;
    return null;
  };

  return (
    <div className="cp-page">
      <div className="cp-card">
        <div className="cp-header">
          {user?.avatar
            ? <img src={user.avatar} alt="" className="avatar avatar-lg cp-avatar" />
            : <div className="avatar-placeholder avatar-lg cp-avatar">{user?.name?.[0]}</div>
          }
          <h2>Complete your profile</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Hi {user?.name?.split(' ')[0]}! Choose a username and fill in your details.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="cp-form" noValidate>

          {/* Username */}
          <div className="form-group">
            <label className="label">
              <AtSign size={14} style={{ display: 'inline', marginRight: 5 }} />
              Choose your Username
            </label>

            <div className="username-input-wrap">
              <span className="username-prefix">@</span>
              <input
                className={'input username-input ' + (errors.username ? 'input-error' : '')}
                name="username"
                placeholder="your_username"
                value={form.username}
                onChange={handleChange}
                maxLength={20}
                autoComplete="off"
              />
              <span className="username-status-icon"><StatusIcon /></span>
            </div>

            {/* Status messages */}
            {usernameStatus === 'available' && (
              <span style={{ fontSize: '0.78rem', color: 'var(--success)' }}>
                ✓ @{form.username} is available!
              </span>
            )}
            {usernameStatus === 'taken' && (
              <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                ✗ @{form.username} is taken. Try one of the suggestions below.
              </span>
            )}
            {usernameStatus === 'invalid' && (
              <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                Only lowercase letters, numbers and underscores (3–20 chars).
              </span>
            )}
            {errors.username && <span className="error-text">{errors.username}</span>}

            {/* Suggestions from server — guaranteed available */}
            <div className="username-suggestions-row">
              <span className="helper-text">Suggestions (all available):</span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ padding: '2px 8px', fontSize: '0.72rem' }}
                onClick={fetchSuggestions}
                disabled={loadingSugg}
              >
                {loadingSugg
                  ? <Loader size={12} className="spin" />
                  : <><RefreshCw size={12} /> Refresh</>
                }
              </button>
            </div>

            <div className="username-suggestions">
              {loadingSugg ? (
                <div className="spinner" style={{ width: 16, height: 16 }} />
              ) : suggestions.map(s => (
                <button
                  key={s}
                  type="button"
                  className={'suggestion-chip ' + (form.username === s ? 'active' : '')}
                  onClick={() => {
                    setForm(f => ({ ...f, username: s }));
                    setUsernameStatus('available');
                  }}
                >
                  @{s}
                </button>
              ))}
            </div>

            <span className="helper-text">
              Username is permanent after saving. Only lowercase, numbers, underscores allowed.
            </span>
          </div>

          {/* Roll Number */}
          <div className="form-group">
            <label className="label">
              <Hash size={14} style={{ display: 'inline', marginRight: 5 }} />
              University Roll Number
            </label>
            <input
              className={'input ' + (errors.rollNumber ? 'input-error' : '')}
              name="rollNumber"
              placeholder="e.g. 220820107034"
              value={form.rollNumber}
              onChange={handleChange}
            />
            {errors.rollNumber && <span className="error-text">{errors.rollNumber}</span>}
          </div>

          {/* Call + WhatsApp */}
          <div className="cp-two-col">
            <div className="form-group">
              <label className="label">
                <Phone size={14} style={{ display: 'inline', marginRight: 5 }} />
                Call Number
              </label>
              <input
                className={'input ' + (errors.callNumber ? 'input-error' : '')}
                name="callNumber"
                placeholder="10-digit number"
                value={form.callNumber}
                onChange={handleChange}
                type="tel"
                maxLength={10}
              />
              {errors.callNumber && <span className="error-text">{errors.callNumber}</span>}
            </div>

            <div className="form-group">
              <label className="label">
                <MessageCircle size={14} style={{ display: 'inline', marginRight: 5 }} />
                WhatsApp
              </label>
              <input
                className={'input ' + (errors.whatsappNumber ? 'input-error' : '')}
                name="whatsappNumber"
                placeholder="10-digit number"
                value={form.whatsappNumber}
                onChange={handleChange}
                type="tel"
                maxLength={10}
              />
              {errors.whatsappNumber && <span className="error-text">{errors.whatsappNumber}</span>}
            </div>
          </div>

          {/* Location */}
          <div className="form-group">
            <label className="label">
              <MapPin size={14} style={{ display: 'inline', marginRight: 5 }} />
              Your Location on Campus
            </label>
            <input
              className={'input ' + (errors.location ? 'input-error' : '')}
              name="location"
              placeholder="e.g. Boys Hostel Block B, Room 210"
              value={form.location}
              onChange={handleChange}
            />
            {errors.location && <span className="error-text">{errors.location}</span>}
          </div>

          {/* Gender */}
          <div className="form-group">
            <label className="label">Gender</label>
            <select
              className="input"
              name="gender"
              value={form.gender}
              onChange={handleChange}
            >
              <option value="prefer_not_to_say">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading || usernameStatus === 'checking' || usernameStatus === 'taken' || usernameStatus === 'invalid'}
          >
            {loading
              ? <><div className="spinner" /> Saving...</>
              : 'Save & Enter GLA Exchange →'
            }
          </button>
        </form>
      </div>
    </div>
  );
}